import { useTheme } from '@emotion/react'
import {
    Product,
    Profile,
    RequiredAccess,
    requiredAccessValue,
} from '@news-mono/web-common'
import React from 'react'
import { FundingType } from '../../../templates/Publication/lib/get-funding-type'
import { TextLink } from '../../../typography/TextLink/TextLink'
import { TimestampFormat } from '../../../typography/Timestamp/Timestamp'
import { themedValue } from '../../../__styling/themed-value'
import {
    StyledBylineText,
    StyledBylineTextWrapper,
    StyledCommentsIcon,
    StyledCommentsWrapper,
    StyledMetaDataWrapper,
    StyledPremiumIcon,
    StyledPremiumWrapper,
} from '../Byline.styled'
import { BylineContent } from './BylineContent'
import { BylineTimestamp } from './BylineTimestamp'
import { BylineTimestampUpdated } from './BylineTimestampUpdated'

interface BylineTextProps {
    inlineTimestamp?: boolean
    profile: Profile[] | undefined
    fundingType?: FundingType
    source: string | undefined
    timestamp: string | undefined
    timestampUpdated: string | undefined
    text: string | undefined
    requiredAccess?: RequiredAccess
    allowCommenting: boolean
}

export const BylineText: React.FC<BylineTextProps> = ({
    inlineTimestamp = false,
    profile,
    fundingType,
    source,
    timestamp,
    timestampUpdated,
    text,
    requiredAccess,
    allowCommenting,
}) => {
    const theme = useTheme()

    const timestampPublishedFormat = themedValue(theme, {
        thewest: 'dateTimeShortDay',
        sevennews: timestampUpdated
            ? 'dayMonthYear'
            : 'customSevenNewsDateTime',
        perthnow: 'default',
    }) as TimestampFormat

    const timestampUpdatedFormat = themedValue(theme, {
        sevennews: 'customSevenNewsDateTime',
        fallback: 'dateTimeOnly',
    }) as TimestampFormat

    const isRestricted = requiredAccess && requiredAccess.level !== 'anonymous'

    const canShowComments =
        allowCommenting &&
        theme.kind === Product.TheWest &&
        theme.section === 'default'

    return (
        <StyledBylineText>
            <StyledBylineTextWrapper>
                <BylineContent
                    profiles={profile}
                    inlineTimestamp={inlineTimestamp}
                    fundingType={fundingType}
                    source={source}
                    timestamp={timestamp}
                    timestampUpdated={timestampUpdated}
                    timestampFormat={timestampPublishedFormat}
                    timestampUpdatedFormat={timestampUpdatedFormat}
                    text={text}
                />
            </StyledBylineTextWrapper>
            {(timestamp || isRestricted) && (
                <StyledMetaDataWrapper>
                    {!inlineTimestamp && (
                        <React.Fragment>
                            <BylineTimestamp
                                timestamp={timestamp}
                                timestampFormat={timestampPublishedFormat}
                            />
                            {timestampUpdated && (
                                <BylineTimestampUpdated
                                    timestamp={timestampUpdated}
                                    timestampUpdatedFormat={
                                        timestampUpdatedFormat
                                    }
                                />
                            )}
                        </React.Fragment>
                    )}
                    {canShowComments && (
                        <StyledCommentsWrapper>
                            <StyledCommentsIcon />
                            <TextLink href="#coral_thread">Comments</TextLink>
                        </StyledCommentsWrapper>
                    )}
                    {requiredAccessValue(requiredAccess, {
                        subscriber: (
                            <StyledPremiumWrapper>
                                <StyledPremiumIcon width={17} height={17} />
                            </StyledPremiumWrapper>
                        ),
                        anonymous: undefined,
                    })}
                </StyledMetaDataWrapper>
            )}
        </StyledBylineText>
    )
}
